import { AsyncPipe, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  WritableSignal
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ApolloQueryResult } from '@apollo/client/core';
import { FilterComponent } from '@app/catalog/filter/filter.component';
import { BREADCRUMB_BRAND_QUERY, SERIES_LIST_QUERY } from '@shared/consts/graphql';
import { Brand, Series } from '@shared/graphql/generated';
import { CarItem } from '@shared/models/car-item';
import { FilterValue } from '@shared/models/filter-value';
import { SelectOption } from '@shared/models/select-option';
import { ViewHistoryItem } from '@shared/models/view-history-item';
import { CarItemTransformService } from '@shared/services/car-item-transform.service';
import { DataStateService } from '@shared/services/data-state-service.service';
import { FilterValueService } from '@shared/services/filter-value.service';
import { ViewHistoryStoreService } from '@shared/stores/view-history-store.service';
import { BreadcrumbsComponent } from '@shared/ui-components/breadcrumbs/breadcrumbs.component';
import { BreadcrumbItem } from '@shared/ui-components/breadcrumbs/shared/breadcrumb-item';
import { CarListComponent } from '@shared/ui-components/car-list/car-list.component';
import { PaginationComponent } from '@shared/ui-components/pagination/pagination.component';
import {
  QuestionsFullWidthFormComponent
} from '@shared/widgets/questions-full-width-form/questions-full-width-form.component';
import { ViewHistoryComponent } from '@shared/widgets/view-history/view-history.component';
import { Apollo } from 'apollo-angular';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap
} from 'rxjs';

const INITIAL_BREADCRUMB = { routerLink: '/catalog', label: $localize`Каталог` };
const CAR_LIST_ID = 'carList';

interface BreadcrumbsData {
  brand_id?: string;
}

@Component({
  selector: 'evland-catalog',
  templateUrl: './catalog.component.html',
  styleUrls: [ './catalog.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    BreadcrumbsComponent,
    FilterComponent,
    CarListComponent,
    PaginationComponent,
    ViewHistoryComponent,
    QuestionsFullWidthFormComponent,
  ],
})
export class CatalogComponent implements OnInit, OnDestroy {
  CAR_LIST_ID = CAR_LIST_ID;
  breadcrumbs: WritableSignal<BreadcrumbItem[]> = signal([ INITIAL_BREADCRUMB ]);
  sortOptions: SelectOption[] = [
    { value: 'views:DESC', label: `${$localize`За популярністю`} ▼` },
    { value: 'views:ASC', label: `${$localize`За популярністю`} ▲` },
    { value: 'created_at:DESC', label: `${$localize`За актуальністю`} ▼` },
    { value: 'created_at:ASC', label: `${$localize`За актуальністю`} ▲` },
    { value: 'name:ASC', label: `${$localize`За назвою моделі`} ▲` },
    { value: 'name:DESC', label: `${$localize`За назвою моделі`} ▼` },
  ];
  pageSizes: number[] = [ 12, 24, 36, 48, 60 ];
  historyItems$: Observable<ViewHistoryItem[]>;
  initValues: WritableSignal<any> = signal({
    filter: {},
    sort: this.sortOptions[0].value,
    paginator: {
      page: 1,
      pageSize: this.pageSizes[0],
    },
  });
  series: WritableSignal<CarItem[]> = signal([]);
  total: WritableSignal<number> = signal(0);
  isFirstDatasetLoad: WritableSignal<boolean> = signal(true);
  isLoading: WritableSignal<boolean> = signal(true);
  isUpdating: WritableSignal<boolean> = signal(false);

  private isServer: boolean;

  private breadcrumbsDataSubject = new BehaviorSubject<BreadcrumbsData>({});
  breadcrumbsData$ = this.breadcrumbsDataSubject.asObservable();

  private filterSubject = new BehaviorSubject<FilterValue>({});
  filter$ = this.filterSubject.asObservable();

  private sortSubject = new BehaviorSubject<string>(this.sortOptions[0].value);
  sort$ = this.sortSubject.asObservable();

  private pageSubject = new BehaviorSubject<number>(1);
  page$ = this.pageSubject.asObservable();

  private pageSizeSubject = new BehaviorSubject<number>(this.pageSizes[0]);
  pageSize$ = this.pageSizeSubject.asObservable();

  private readonly destroyed$ = new Subject<void>();

  #apollo = inject(Apollo);
  #carItemTransformService = inject(CarItemTransformService);
  #dataStateService = inject(DataStateService);
  #filterValueService = inject(FilterValueService);
  #route = inject(ActivatedRoute);
  #router = inject(Router);
  #titleService = inject(Title);
  #viewHistoryStore = inject(ViewHistoryStoreService);

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
  ) {
    this.isServer = isPlatformServer(this.platformId);
  }

  ngOnInit(): void {
    this.initBreadcrumbs();
    this.initData();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onFilterChange(filter: FilterValue): void {
    const { brand_id } = filter;

    this.breadcrumbsDataSubject.next({ brand_id });
    this.filterSubject.next(filter);
    this.pageSubject.next(1);
  }

  onPageChange(page: number): void {
    this.pageSubject.next(page);
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSizeSubject.next(pageSize);
    this.pageSubject.next(1);
  }

  onSortChange(sort: string): void {
    this.sortSubject.next(sort);
    this.pageSubject.next(1);
  }

  scrollIntoView(id: string): void {
    if (this.isServer) {
      return;
    }

    const el = document.getElementById(id);
    const yOffset = -105;
    const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;

    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  private initBreadcrumbs(): void {
    const getGraphqlRequestResult = (query, id, resultKey) => this.#dataStateService.checkAndGetData(
      `catalogRootBreadcrumbs_${resultKey}`,
      this.#apollo.watchQuery({
        query,
        variables: { id }
      }).valueChanges.pipe(map((result: any) => result.data[resultKey])),
      [],
    );

    this.breadcrumbsData$.pipe(
      takeUntil(this.destroyed$),
      switchMap(({ brand_id }) => combineLatest([
        brand_id ? getGraphqlRequestResult(BREADCRUMB_BRAND_QUERY, brand_id, 'brand') : of(null),
      ])),
    ).subscribe(([ brand ]: [ Brand ]) => {
      const name = brand?.name;

      this.breadcrumbs.set([
        INITIAL_BREADCRUMB,
        ...(brand ? [ { routerLink: '/offers', queryParams: { brand_id: brand.id }, label: brand.name } ] : []),
      ]);
      this.#titleService.setTitle(`${!!name ? $localize`Каталог новых електромобілів ${name}` : $localize`Каталог новых електромобілів`} | evland ${new Date().getFullYear()}`);
    });
  }

  private initData(): void {
    this.historyItems$ = this.#viewHistoryStore.list$;

    this.#route.queryParams
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe(queryParams => {
        const { order, order_by, page, page_size, ...filterValue } = queryParams;
        const initValues = {
          filter: filterValue,
          sort: order_by && order ? `${order_by}:${order}` : this.sortOptions[0].value,
          paginator: {
            page: page ? +page : 1,
            pageSize: page_size ? +page_size : this.pageSizes[0],
          },
        };

        this.initValues.set(initValues);
        this.filterSubject.next(initValues.filter);
        this.sortSubject.next(initValues.sort);
        this.pageSubject.next(initValues.paginator.page);
        this.pageSizeSubject.next(initValues.paginator.pageSize);
        this.isFirstDatasetLoad.set(true);
      });

    this.initDataset();
  }

  private initDataset(): void {
    const update$ = combineLatest([
      this.sort$,
      this.page$,
      this.pageSize$,
    ])
      .pipe(
        tap(() => this.isUpdating.set(true))
      );
    const reload$ = this.filter$
      .pipe(
        tap(() => this.isLoading.set(true))
      );

    combineLatest([
      reload$,
      update$,
    ])
      .pipe(
        takeUntil(this.destroyed$),
        debounceTime(100),
        tap(() => this.series.set([])),
        map(([ filterValue, update ]) => ([ filterValue, ...update ])),
        tap(([ filterValue, sortValue, page, pageSize ]: [ FilterValue, string, number, number ]) => {
          const [ order_by, order ] = sortValue.split(':');
          const { brand_id } = filterValue;

          this.breadcrumbsDataSubject.next({ brand_id });

          if (this.isFirstDatasetLoad()) {
            this.isFirstDatasetLoad.set(false);

            return;
          }

          this.#router.navigate(
            [ '/catalog' ],
            {
              queryParams: {
                ...Object.keys(filterValue).reduce((acc, key) => ({ ...acc, ...(!!filterValue[key] ? { [key]: filterValue[key] } : {}) }), {}),
                ...(this.sortOptions[0].value !== `${order_by}:${order}` && {
                  order_by,
                  order,
                }),
                ...(page > 1 && { page }),
                ...(this.pageSizes[0] !== pageSize && { page_size: pageSize }),
              },
            }
          );
        }),
        map(([ filterValue, sortValue, page, pageSize ]: [ FilterValue, string, number, number ]) => ([ this.#filterValueService.toApiValues(filterValue), sortValue, page, pageSize ])),
        switchMap(([ filterValue, sortValue, page, pageSize ]: [ FilterValue, string, number, number ]) => {
          const [ order_by, order ] = sortValue.split(':');

          return this.#dataStateService.checkAndGetData(
            `catalogSearchResult_${page}_${pageSize}_${sortValue}_${Object.keys(filterValue).map((key: string) => `${key}_${filterValue[key]}`).join('_')}`,
            this.#apollo
              .watchQuery({
                query: SERIES_LIST_QUERY,
                variables: {
                  ...filterValue,
                  order_by,
                  order,
                  take: pageSize,
                  skip: pageSize * (page - 1)
                }
              }).valueChanges.pipe(
              map((result: ApolloQueryResult<{ series: Series[]; countSeries: number; }>) => ({
                items: result.data.series || [],
                total: result.data.countSeries || 0,
              })),
            ),
            { items: [], total: 0 },
          );
        }),
      )
      .subscribe(({ items, total }) => {
        if (!!this.total()) {
          this.scrollIntoView(CAR_LIST_ID);
        }

        this.total.set(total);
        this.series.set(items.map(this.#carItemTransformService.transformSeries));
        this.isLoading.set(false);
        this.isUpdating.set(false);
      });
  }
}
